import { useEffect, useState, type ReactNode } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getActor, createActor, updateActor } from "@/services/firebase/actor";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import ImageUpload from "@/components/common/ImageUpload";
import { AdminPage, AdminPageHeader, AdminLoadingSkeleton, AdminErrorAlert } from "@/components/admin-ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";

import ArtistWorkspace from "./ArtistWorkspace";
import ArtistPublicationPanel from "./ArtistPublicationPanel";
import ArtistProfilePanel from "./ArtistProfilePanel";
import ArtistFansPanel from "./ArtistFansPanel";
import ArtistFanPassPanel from "./ArtistFanPassPanel";
import ArtistPayeePanel from "./ArtistPayeePanel";
import ArtistStoryRepliesPanel from "./ArtistStoryRepliesPanel";
import ArtistDmPanel from "./ArtistDmPanel";
import ArtistDmInboxPanel from "./ArtistDmInboxPanel";
import ArtistMembersPanel from "./ArtistMembersPanel";
import ArtistStatusSummary from "./ArtistStatusSummary";
import ArtistWorksPanel from "./ArtistWorksPanel";
import { artistHubEnabled } from "@/services/firebase/artistHub";
import { useArtistFlags } from "@/hooks/useArtistFlags";
import { ARTIST_TAB_KEYS, ARTIST_TAB_LABELS, ArtistTabKey, DEFAULT_ARTIST_TAB, parseArtistTab } from "@/lib/artistFlags";

/** U2: a section whose RC flag is off stays collapsed behind 「展開」 (staff can still clean up / intervene). */
function FlagGatedSection({ enabled, notice, children }: { enabled: boolean; notice: string; children: ReactNode }) {
  const [expanded, setExpanded] = useState(false);
  if (enabled) return <>{children}</>;
  return (
    <div className="mb-6 space-y-4">
      <Alert>
        <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
          <span>{notice}</span>
          <Button type="button" variant="outline" size="sm" aria-expanded={expanded} onClick={() => setExpanded((v) => !v)}>
            {expanded ? "收合" : "展開"}
          </Button>
        </AlertDescription>
      </Alert>
      {expanded && children}
    </div>
  );
}

const actorSchema = z.object({
  name: z.string().min(1, "演員名稱為必填"),
  gender: z.enum(["male", "female", "other"]).optional(),
  birthdate: z.date().optional().nullable(),
  nationality: z.string().optional(),
  photoURL: z.string().optional(),
  biography: z.string().optional(),
});

type ActorFormData = z.infer<typeof actorSchema>;

export default function ActorEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [saveError, setSaveError] = useState<string | null>(null);
  const isNew = id === "new" || id === undefined;
  const actualId = isNew ? undefined : id?.replace("/edit", "").replace("edit", "");
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseArtistTab(searchParams.get("tab"));
  const setTab = (value: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const key = parseArtistTab(value);
        if (key === DEFAULT_ARTIST_TAB) next.delete("tab");
        else next.set("tab", key);
        return next;
      },
      { replace: true },
    );
  const flags = useArtistFlags();

  // 載入演員數據
  const { data: actor, isLoading } = useQuery({
    queryKey: ["actor", actualId],
    queryFn: () => getActor(actualId!),
    enabled: !isNew && !!actualId,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
    watch,
  } = useForm<ActorFormData>({
    resolver: zodResolver(actorSchema),
    defaultValues: {
      name: "",
      gender: undefined,
      birthdate: undefined,
      nationality: "",
      photoURL: "",
      biography: "",
    },
  });

  const photoURL = watch("photoURL");
  const birthdate = watch("birthdate");
  const gender = watch("gender");

  // 格式化日期為 YYYY-MM-DD 格式（用於 date input）
  const formatDateForInput = (date: Date | undefined): string => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // 初始化表單數據
  useEffect(() => {
    if (actor && !isNew) {
      reset({ name: actor.name || "", gender: actor.gender,
        birthdate: actor.birthdate ? new Date(actor.birthdate) : undefined,
        nationality: actor.nationality || "", photoURL: actor.photoURL || "", biography: actor.biography || "" });
    }
  }, [actor, reset, isNew]);

  const mutation = useMutation({
    mutationFn: async (data: ActorFormData) => {
      if (isNew) {
        return await createActor({
          name: data.name,
          gender: data.gender,
          birthdate: data.birthdate || undefined,
          nationality: data.nationality,
          photoURL: data.photoURL,
          biography: data.biography,
        });
      } else {
        if (!actualId) {
          throw new Error("缺少演員 ID");
        }
        await updateActor(actualId, {
          name: data.name,
          gender: data.gender,
          birthdate: data.birthdate || undefined,
          nationality: data.nationality,
          photoURL: data.photoURL,
          biography: data.biography,
        });
        return actualId;
      }
    },
    onSuccess: (savedId) => {
      queryClient.invalidateQueries({ queryKey: ["actors"] });
      queryClient.invalidateQueries({ queryKey: ["actor", savedId] });
      setSaveError(null);
      navigate("/actors");
    },
    onError: (error) => {
      console.error("儲存演員失敗:", error);
      setSaveError(error instanceof Error ? error.message : "未知錯誤");
    },
  });

  const onSubmit = (data: ActorFormData) => {
    mutation.mutate(data);
  };

  const handleImageUpload = (url: string) => {
    setValue("photoURL", url, { shouldDirty: true, shouldValidate: true });
  };

  if (!isNew && isLoading) {
    return (
      <AdminPage>
        <AdminLoadingSkeleton variant="mixed" />
      </AdminPage>
    );
  }

  const actorForm = (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* 錯誤訊息 */}
      {saveError && (
        <AdminErrorAlert
          message={`儲存失敗：${saveError}`}
          onDismiss={() => setSaveError(null)}
        />
      )}

      {/* 基本資訊 */}
      <Card>
        <CardHeader>
          <CardTitle>基本資訊</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">
              演員名稱 <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              {...register("name")}
              placeholder="例如：張三"
              className={errors.name ? "border-destructive" : ""}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>性別</Label>
            <Select
              value={gender || ""}
              onValueChange={(value) =>
                setValue("gender", value as "male" | "female" | "other" | undefined)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="請選擇" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">男</SelectItem>
                <SelectItem value="female">女</SelectItem>
                <SelectItem value="other">其他</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="birthdate">出生日期</Label>
            <Input
              id="birthdate"
              type="date"
              value={birthdate ? formatDateForInput(birthdate) : ""}
              onChange={(e) => {
                const value = e.target.value;
                setValue("birthdate", value ? new Date(value) : undefined);
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="nationality">國籍</Label>
            <Input
              id="nationality"
              {...register("nationality")}
              placeholder="例如：台灣"
            />
          </div>
        </CardContent>
      </Card>

      {/* 照片 */}
      <Card>
        <CardHeader>
          <CardTitle>照片</CardTitle>
        </CardHeader>
        <CardContent>
          <ImageUpload
            value={photoURL}
            onChange={handleImageUpload}
            storagePath="actor_photos"
            label="演員照片"
            required={false}
          />
        </CardContent>
      </Card>

      {/* 簡介 */}
      <Card>
        <CardHeader>
          <CardTitle>簡介</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="biography">個人簡介</Label>
            <Textarea
              id="biography"
              {...register("biography")}
              rows={6}
              placeholder="輸入演員的個人簡介..."
            />
          </div>
        </CardContent>
      </Card>

      {/* 操作按鈕 */}
      <div className="flex gap-3 justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate("/actors")}
        >
          取消
        </Button>
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          {mutation.isPending ? "儲存中..." : "儲存"}
        </Button>
      </div>
    </form>
  );

  const showArtistTabs = artistHubEnabled && !!actualId && !!actor;
  const fansNotice = "鐵粉功能目前未開放（Remote Config artist_fans_enabled = false）。以下僅供緊急處置與清理。";
  const dmNotice = flags.fansEnabled
    ? "私訊功能目前未開放（Remote Config artist_dm_enabled = false）。以下僅供緊急處置與清理。"
    : "私訊需要鐵粉功能同時開放（Remote Config artist_fans_enabled = false）。以下僅供緊急處置與清理。";
  const tabSuffix = (key: ArtistTabKey) =>
    key === "actor" && isDirty ? " •" : key === "fans" && !flags.fansEnabled ? "（未開放）" : "";

  return (
    <AdminPage>
      <AdminPageHeader
        title={isNew ? "新增藝人" : `藝人：${actor?.name || ""}`}
        actions={
          <Button variant="outline" onClick={() => navigate("/actors")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            返回
          </Button>
        }
      />

      {!showArtistTabs ? (
        actorForm
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4 h-auto flex-wrap justify-start">
            {ARTIST_TAB_KEYS.map((key) => (
              <TabsTrigger key={key} value={key}>
                {ARTIST_TAB_LABELS[key]}
                {tabSuffix(key)}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview">
            <ArtistPublicationPanel key={actualId} artistId={actualId} dirty={isDirty || mutation.isPending} actorName={actor.name} actorBio={actor.biography} />
            <ArtistStatusSummary key={`summary-${actualId}`} artistId={actualId} />
            <ArtistWorksPanel key={`works-${actualId}`} artistId={actualId} />
          </TabsContent>

          <TabsContent value="profile">
            <ArtistProfilePanel key={`profile-${actualId}`} artistId={actualId} />
          </TabsContent>

          <TabsContent value="members">
            <ArtistMembersPanel key={`members-${actualId}`} artistId={actualId} />
          </TabsContent>

          <TabsContent value="content">
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>內容工作區</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">圖文與 Vlog 存稿、送審、發布與審核；影片與封面由 App 上傳。</p>
                <ArtistWorkspace key={`workspace-${actualId}`} artistId={actualId} fansEnabled={flags.fansEnabled} />
              </CardContent>
            </Card>
            <ArtistStoryRepliesPanel key={`story-replies-${actualId}`} artistId={actualId} />
          </TabsContent>

          <TabsContent value="fans">
            <FlagGatedSection enabled={flags.fansEnabled} notice={fansNotice}>
              <ArtistFanPassPanel key={`fan-pass-${actualId}`} artistId={actualId} artistName={actor.name} />
              <ArtistFansPanel key={`fans-${actualId}`} artistId={actualId} />
              <ArtistPayeePanel key={`payee-${actualId}`} artistId={actualId} />
            </FlagGatedSection>
            <FlagGatedSection enabled={flags.dmEnabled} notice={dmNotice}>
              <ArtistDmPanel key={`dm-${actualId}`} artistId={actualId} />
              <ArtistDmInboxPanel key={`dm-inbox-${actualId}`} artistId={actualId} />
            </FlagGatedSection>
          </TabsContent>

          {/* forceMount keeps the form (and any unsaved edits) mounted while another tab is open. */}
          <TabsContent value="actor" forceMount className="data-[state=inactive]:hidden">
            {actorForm}
          </TabsContent>
        </Tabs>
      )}
    </AdminPage>
  );
}
